/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * CourseEditorModal
 * -----------------
 * The course drawer, full screen, over the event form.
 *
 * Full screen because drawing a 21K one click per corner needs the whole
 * window — a map squeezed into a form column is a map you cannot draw on.
 *
 * Edits a COPY of the course. "Use this course" hands it back; Cancel or Escape
 * throws it away, so an organizer can explore a change without touching the
 * course already on the activity.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RouteDrawer from './RouteDrawer';
import readGpxFile from '../../athlohive/gpx/readGpxFile';
import ORGANIZER_TOKENS from '../../../theme/organizerTokens';

export default function CourseEditorModal({ title, initialPoints = [], onCancel, onSave }) {
  const [points, setPoints] = useState(initialPoints);
  const [fitKey, setFitKey] = useState(0);
  const [message, setMessage] = useState(null);
  const dialogRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (event) => { if (event.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    // The form behind must not scroll while the map owns the wheel.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onCancel]);

  const onPickFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const result = await readGpxFile(file);
    if (!result) {
      setMessage(`${file.name} has no track, route or waypoints to read.`);
      return;
    }
    setPoints(result.points);
    setFitKey((key) => key + 1);
    setMessage(`Loaded ${file.name}. Adjust it, then use this course.`);
  };

  return createPortal(
    // Portaled to <body>, outside .org-shell where the --org-* tokens live, so
    // the modal carries its own copy of them.
    <div className="orgr-modal" role="presentation" style={ORGANIZER_TOKENS}>
      <div
        className="orgr-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="orgr-modal-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="orgr-modal__head">
          <div style={{ minWidth: 0 }}>
            <h2 id="orgr-modal-title" className="orgr-h2">{title || 'Draw the course'}</h2>
            <p className="orgr-note">
              Put a point at every turn. The line, distance, start and finish go onto the activity,
              and a GPX file is attached when you save the event.
            </p>
          </div>
          <div className="orgr-modal__actions">
            <button type="button" className="org-btn ghost" onClick={() => fileRef.current?.click()}>
              <i className="bi bi-upload" aria-hidden="true" /> Start from a GPX
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".gpx,application/gpx+xml"
              className="orgr-fileinput"
              onChange={onPickFile}
              tabIndex={-1}
              aria-hidden="true"
            />
            <button type="button" className="org-btn ghost" onClick={onCancel}>Cancel</button>
            <button
              type="button"
              className="org-btn"
              onClick={() => onSave(points)}
              // One point is not a course; zero is only meaningful as "remove the
              // course that was there", so an empty course that started empty has
              // nothing to use.
              disabled={points.length === 1 || (points.length === 0 && initialPoints.length === 0)}
              title={points.length === 1 ? 'A course needs at least two points' : undefined}
            >
              Use this course
            </button>
          </div>
        </header>
        {message && <p className="orgr-status" role="status">{message}</p>}
        <div className="orgr-modal__body">
          <RouteDrawer
            points={points}
            onChange={setPoints}
            fitKey={fitKey}
            height="calc(100vh - 230px)"
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
